import {
  Building2,
  Calendar,
  Clock,
  CreditCard,
  Hash,
  Mail,
  MapPin,
  MessageSquare,
  Phone,
  Shield,
  Tag,
  User,
  Wallet,
} from "lucide-react";
import { formatDate, formatDateTime, formatMoneyValue } from "../../data/mock/dashboardData";
import telecelLogo from "../../assets/payment/telecel.png";
import mtnLogo from "../../assets/payment/mtn.png";
import airtelTigoLogo from "../../assets/payment/airteltigo.png";
import TableActionsMenu from "./TableActionsMenu";
import {
  tableActionClass,
  tableActionCompactClass,
  tableActionInfoClass,
  tableActionMutedClass,
  tableActionPrimaryClass,
} from "./tableActionStyles";

export {
  tableActionClass,
  tableActionCompactClass,
  tableActionInfoClass,
  tableActionMutedClass,
  tableActionPrimaryClass,
} from "./tableActionStyles";

const ID_KEY_PATTERNS = [
  /^id$/i,
  /_id$/i,
  /Id$/,
  /^account_number$/i,
  /^accountNumber$/i,
  /^paymentref$/i,
  /^case_id$/i,
  /^reference$/i,
  /^msisdn$/i,
];
const NAME_KEYS = /^(customer_name|customer|name|assigned|agent|recordedby|assignedcounsel|uploadedby)$/i;
const PHONE_KEYS = /^phone$/i;
const CURRENCY_KEYS =
  /^(amount|amountOwed|amountPaid|amount_owed|amount_paid|outstanding|owed|commission|expected|received|totalowed|totalOwed|outstanding_amount|instalmentamount|instalmentAmount|totalamount|totalAmount|recoverytoday|recoveryToday|collected)$/i;
const DATE_KEYS = /(duedate|nextdue|^date$|dob|dateofbirth)/i;
const DATETIME_KEYS = /(_at$|loggedat|sentat|startedat|receivedat|matchedat|uploadedat|escalatedat|recordedat|generatedat|closedat|updatedat|createdat|lastlogin|assignedat)/i;
const NUMBER_KEYS = /^(dayslate|daysoverdue|days|accounts|duration|instalments|paidinstalments|invalidrows|casescreated|casesmatched|rowindex)$/i;
const CHANNEL_KEYS = /^(channel|lastcontact|template|method|paymenttype|apitype|menustep)$/i;
const NETWORK_KEYS = /^network$/i;
const REGION_KEYS = /^(region|segment|priority|product_type|producttype|repaymentbehavior|prioritytier|closureReason)$/i;

const AVATAR_PALETTE = [
  "from-primary to-[#EF4444]",
  "from-[#c9a209] to-accent",
  "from-emerald-600 to-emerald-500",
  "from-violet-600 to-violet-500",
  "from-sky-600 to-sky-500",
  "from-rose-600 to-rose-500",
];

export const HEADER_ICONS = {
  id: Hash,
  name: User,
  phone: Phone,
  currency: Wallet,
  date: Calendar,
  number: Clock,
  channel: MessageSquare,
  region: MapPin,
  text: Tag,
  badge: Tag,
};

const CHANNEL_CONFIG = {
  sms: { icon: MessageSquare, label: "SMS", bg: "bg-sky-50", text: "text-sky-700", ring: "ring-sky-600/10" },
  "text message": { icon: MessageSquare, label: "SMS", bg: "bg-sky-50", text: "text-sky-700", ring: "ring-sky-600/10" },
  whatsapp: { icon: MessageSquare, label: "WhatsApp", bg: "bg-emerald-50", text: "text-emerald-700", ring: "ring-emerald-600/10" },
  email: { icon: Mail, label: "Email", bg: "bg-violet-50", text: "text-violet-700", ring: "ring-violet-600/10" },
  "email reminder": { icon: Mail, label: "Email", bg: "bg-violet-50", text: "text-violet-700", ring: "ring-violet-600/10" },
  "mobile money": { icon: Wallet, label: "Mobile Money", bg: "bg-primary/5", text: "text-primary", ring: "ring-primary/10" },
  "phone call": { icon: Phone, label: "Phone", bg: "bg-orange-50", text: "text-orange-700", ring: "ring-orange-600/10" },
  "voice calls": { icon: Phone, label: "Voice", bg: "bg-orange-50", text: "text-orange-700", ring: "ring-orange-600/10" },
  "payment plan": { icon: Calendar, label: "Payment plan", bg: "bg-indigo-50", text: "text-indigo-700", ring: "ring-indigo-600/10" },
  "bank transfer": { icon: Building2, label: "Bank transfer", bg: "bg-slate-50", text: "text-slate-700", ring: "ring-slate-600/10" },
  card: { icon: CreditCard, label: "Card", bg: "bg-pink-50", text: "text-pink-700", ring: "ring-pink-600/10" },
  cash: { icon: Wallet, label: "Cash", bg: "bg-lime-50", text: "text-lime-800", ring: "ring-lime-600/10" },
  "partial payment": { icon: Wallet, label: "Partial", bg: "bg-teal-50", text: "text-teal-700", ring: "ring-teal-600/10" },
  instalment: { icon: Calendar, label: "Instalment", bg: "bg-indigo-50", text: "text-indigo-700", ring: "ring-indigo-600/10" },
};

function matchesIdKey(key) {
  return ID_KEY_PATTERNS.some((p) => p.test(key));
}

export function inferCellType(key) {
  const k = String(key);
  if (NETWORK_KEYS.test(k)) return "network";
  if (CHANNEL_KEYS.test(k)) return "channel";
  if (REGION_KEYS.test(k)) return "region";
  if (PHONE_KEYS.test(k)) return "phone";
  if (CURRENCY_KEYS.test(k)) return "currency";
  if (DATE_KEYS.test(k)) return "date";
  if (DATETIME_KEYS.test(k)) return "datetime";
  if (NUMBER_KEYS.test(k)) return "number";
  if (NAME_KEYS.test(k)) return "name";
  if (matchesIdKey(k)) return "id";
  return "text";
}

export function getCellAlign(type) {
  if (type === "currency" || type === "number") return "right";
  if (type === "badge") return "center";
  return "left";
}

const ALIGN_CLASS = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
};

export function getAlignClass(align) {
  return ALIGN_CLASS[align] ?? ALIGN_CLASS.left;
}

export function getHeaderIcon(column) {
  if (column.headerIcon) return column.headerIcon;
  const type = column.type ?? inferCellType(column.key);
  return HEADER_ICONS[type] ?? null;
}

function isEmpty(value) {
  return value == null || value === "" || value === "—";
}

function getInitials(name) {
  return String(name)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

function avatarGradient(name) {
  const code = String(name).split("").reduce((s, c) => s + c.charCodeAt(0), 0);
  return AVATAR_PALETTE[code % AVATAR_PALETTE.length];
}

function resolveChannel(value) {
  const key = String(value).toLowerCase().trim();
  if (CHANNEL_CONFIG[key]) return CHANNEL_CONFIG[key];
  if (key.includes("whatsapp")) return CHANNEL_CONFIG.whatsapp;
  if (key.includes("sms") || key.includes("text")) return CHANNEL_CONFIG.sms;
  if (key.includes("email") || key.includes("mail")) return CHANNEL_CONFIG.email;
  if (key.includes("mobile") || key.includes("momo")) return CHANNEL_CONFIG["mobile money"];
  if (key.includes("bank")) return CHANNEL_CONFIG["bank transfer"];
  if (key.includes("phone") || key.includes("voice") || key.includes("call"))
    return CHANNEL_CONFIG["phone call"];
  if (key.includes("plan") || key.includes("instal")) return CHANNEL_CONFIG["payment plan"];
  return {
    icon: Tag,
    label: value,
    bg: "bg-primary/[0.04]",
    text: "text-primary/70",
    ring: "ring-primary/10",
  };
}

const NETWORK_LOGOS = {
  mtn: { logo: mtnLogo, label: "MTN MoMo" },
  mtnmomo: { logo: mtnLogo, label: "MTN MoMo" },
  telecel: { logo: telecelLogo, label: "Telecel Cash" },
  telecelcash: { logo: telecelLogo, label: "Telecel Cash" },
  vodafone: { logo: telecelLogo, label: "Telecel Cash" },
  airteltigo: { logo: airtelTigoLogo, label: "AirtelTigo Money" },
  airtel: { logo: airtelTigoLogo, label: "AirtelTigo Money" },
  at: { logo: airtelTigoLogo, label: "AirtelTigo Money" },
};

function resolveNetwork(value) {
  const key = String(value || "")
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
  if (!key) return null;
  if (NETWORK_LOGOS[key]) return NETWORK_LOGOS[key];
  if (key.includes("mtn")) return NETWORK_LOGOS.mtn;
  if (key.includes("telecel") || key.includes("vodafone")) return NETWORK_LOGOS.telecel;
  if (key.includes("airtel") || key === "at") return NETWORK_LOGOS.airteltigo;
  return null;
}

export function NetworkChip({ value }) {
  if (isEmpty(value)) return <span className="text-primary/25">—</span>;
  const network = resolveNetwork(value);
  if (!network) return <ChannelChip value={value} />;
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-lg border border-primary/10 bg-white px-2 py-1">
      <img
        src={network.logo}
        alt={network.label}
        className="h-7 w-7 shrink-0 rounded-md object-contain"
      />
      <span className="text-xs font-semibold text-primary">{network.label}</span>
    </span>
  );
}

export function ChannelChip({ value }) {
  if (isEmpty(value)) return <span className="text-primary/25">—</span>;
  if (resolveNetwork(value)) return <NetworkChip value={value} />;
  const cfg = resolveChannel(value);
  const Icon = cfg.icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-[11px] font-semibold ring-1 ring-inset ${cfg.bg} ${cfg.text} ${cfg.ring}`}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" strokeWidth={2.25} />
      <span className="whitespace-nowrap">{cfg.label}</span>
    </span>
  );
}

export function AvatarName({ name, sub }) {
  if (isEmpty(name)) return <span className="text-primary/25">—</span>;
  return (
    <div className="flex min-w-0 items-center gap-2.5">
      <span
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br ${avatarGradient(name)} text-[10px] font-bold text-white shadow-sm ring-2 ring-white`}
      >
        {getInitials(name)}
      </span>
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-primary">{name}</p>
        {sub && <p className="truncate text-[11px] text-primary/40">{sub}</p>}
      </div>
    </div>
  );
}

export function TableActionCell({ children, actions }) {
  if (actions?.length) {
    return <TableActionsMenu actions={actions} />;
  }

  return <div className="flex flex-nowrap items-center gap-1.5">{children}</div>;
}

export function TableActionButton({ variant = "default", className = "", children, ...props }) {
  const variantClass =
    variant === "primary"
      ? tableActionPrimaryClass
      : variant === "info"
        ? tableActionInfoClass
        : variant === "muted"
          ? tableActionMutedClass
          : variant === "compact"
            ? tableActionCompactClass
            : tableActionClass;

  return (
    <button type="button" className={`${variantClass} ${className}`.trim()} {...props}>
      {children}
    </button>
  );
}

export function RegionPill({ value }) {
  if (isEmpty(value)) return <span className="text-primary/25">—</span>;
  return (
    <span className="inline-flex max-w-[200px] items-center gap-1.5 truncate rounded-lg bg-primary/[0.04] px-2 py-1 text-[11px] font-medium text-primary/70 ring-1 ring-primary/[0.06]">
      <MapPin className="h-3 w-3 shrink-0 text-primary/35" />
      <span className="truncate">{value}</span>
    </span>
  );
}

const ROLE_STYLES = {
  owner: {
    icon: Shield,
    bg: "bg-violet-50",
    text: "text-violet-700",
    ring: "ring-violet-600/10",
  },
  manager: {
    icon: Shield,
    bg: "bg-primary/[0.06]",
    text: "text-primary",
    ring: "ring-primary/10",
  },
  analyst: {
    icon: User,
    bg: "bg-sky-50",
    text: "text-sky-700",
    ring: "ring-sky-600/10",
  },
  viewer: {
    icon: User,
    bg: "bg-slate-50",
    text: "text-slate-600",
    ring: "ring-slate-500/10",
  },
  "institution admin": {
    icon: Shield,
    bg: "bg-violet-50",
    text: "text-violet-700",
    ring: "ring-violet-600/10",
  },
  "collections manager": {
    icon: Shield,
    bg: "bg-primary/[0.06]",
    text: "text-primary",
    ring: "ring-primary/10",
  },
};

export function RoleBadge({ role }) {
  if (isEmpty(role)) return <span className="text-primary/25">—</span>;
  const key = String(role).toLowerCase();
  const style = ROLE_STYLES[key] ?? {
    icon: User,
    bg: "bg-primary/[0.04]",
    text: "text-primary/70",
    ring: "ring-primary/[0.06]",
  };
  const Icon = style.icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg px-2 py-1 text-[11px] font-semibold ring-1 ring-inset ${style.bg} ${style.text} ${style.ring}`}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" strokeWidth={2.25} />
      {role}
    </span>
  );
}

export function DepartmentPill({ value }) {
  if (isEmpty(value)) return <span className="text-primary/25">—</span>;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-lg bg-primary/[0.03] px-2 py-1 text-[11px] font-medium text-primary/60 ring-1 ring-primary/[0.05]">
      <Building2 className="h-3 w-3 shrink-0 text-primary/30" />
      {value}
    </span>
  );
}

export function renderCellContent(value, type, row) {
  if (isEmpty(value)) {
    return <span className="text-primary/25">—</span>;
  }

  switch (type) {
    case "id":
      return (
        <span className="inline-flex items-center gap-1.5 rounded-lg bg-primary/[0.04] px-2 py-1 font-mono text-[11px] font-medium tracking-wide text-primary/60 ring-1 ring-primary/[0.06]">
          <Hash className="h-3 w-3 shrink-0 text-primary/30" />
          {value}
        </span>
      );
    case "name": {
      const sub = row?.accountNumber ?? row?.account_number ?? undefined;
      return <AvatarName name={value} sub={sub} />;
    }
    case "phone":
      return (
        <span className="inline-flex items-center gap-1.5 font-mono text-[13px] tracking-wide text-primary/75">
          <Phone className="h-3.5 w-3.5 shrink-0 text-primary/30" />
          {value}
        </span>
      );
    case "currency":
      return (
        <span className="inline-flex items-center justify-end gap-1.5 whitespace-nowrap tabular-nums text-sm font-bold text-primary">
          <Wallet className="h-3.5 w-3.5 shrink-0 text-accent-dark/70" />
          {formatMoneyValue(value)}
        </span>
      );
    case "number":
      return (
        <span className="inline-flex items-center justify-end gap-1.5 tabular-nums text-sm font-semibold text-primary/80">
          <Clock className="h-3.5 w-3.5 shrink-0 text-primary/30" />
          {value}
        </span>
      );
    case "date":
      return (
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm text-primary/65">
          <Calendar className="h-3.5 w-3.5 shrink-0 text-primary/30" />
          {typeof value === "string" && value.includes("-") ? formatDate(value) : value}
        </span>
      );
    case "datetime":
      return (
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm text-primary/65">
          <Calendar className="h-3.5 w-3.5 shrink-0 text-primary/30" />
          {value ? formatDateTime(value) : "—"}
        </span>
      );
    case "channel":
      return <ChannelChip value={value} />;
    case "network":
      return <NetworkChip value={value} />;
    case "region":
      return <RegionPill value={value} />;
    case "text":
    default:
      return (
        <span
          className="inline-flex max-w-[220px] items-center gap-1.5 truncate text-sm text-primary/75"
          title={String(value)}
        >
          <Tag className="h-3 w-3 shrink-0 text-primary/25" />
          <span className="truncate">{value}</span>
        </span>
      );
  }
}

export function resolveColumnMeta(column) {
  const type = column.type ?? inferCellType(column.key);
  const align = column.align ?? getCellAlign(type);
  return { type, align };
}
